import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Layout,
    Card,
    Button,
    Typography,
    Form,
    Input,
    Grid,
    List,
    App as AntdApp,
    Row,
    Col,
    theme,
    Progress,
    Skeleton,
    Tag,
    Flex,
    Tooltip
} from 'antd';
import ResponsiveModal from '../Shared/ResponsiveModal/ResponsiveModal';
import {
    DeleteOutlined,
    DownloadOutlined,
    PlusOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../Shared/GlobalContext/ThemeContext';
import { clearAuthData } from '../../Config/ApiConfig';
import { fbLogout } from '../../Utils/facebook';
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import {
    requestDataExport,
    getPreviousExports,
    updateConsents,
    deleteExport,
    deleteAccount
} from './SettingsServices';
import useSocket from '../../Hooks/useSocket';
import useSocketEvent from '../../Hooks/useSocketEvent';
import useSocketSubscription from '../../Hooks/useSocketSubscription';
import { Resource, Action } from '../../Constants/socket';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';
import RowActions from '../Shared/RowActions/RowActions';
import AppTable from '../Shared/AppTable/AppTable';
import { useNavigate } from 'react-router-dom';
import SharedFilterBar from '../Shared/Filters/SharedFilterBar';
import { dataExportFilterFields } from '../Shared/Filters/filterConfig';
import { downloadFile } from '../../Utils/capacitorDownload';

const { useBreakpoint } = Grid;
const { Content } = Layout;
const { Title, Text, Link } = Typography;

const Settings = () => {
    const [form] = Form.useForm();
    const { token } = theme.useToken();
    const { notification } = AntdApp.useApp();
    const { isDarkMode } = useTheme();
    const { t } = useTranslation();
    const { handleError } = useErrorHandler();
    const { loading, setLoading } = useGlobal();
    const navigate = useNavigate();
    const [isExportVisible, setIsExportVisible] = useState(false);
    const [isDeleteVisible, setIsDeleteVisible] = useState(false);
    const [isExportSectionExpanded, setIsExportSectionExpanded] = useState(true);
    const [exportToDelete, setExportToDelete] = useState(null);
    const [isDeleteExportModalVisible, setIsDeleteExportModalVisible] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [deletingExport, setDeletingExport] = useState(false);
    const [deletingAccount, setDeletingAccount] = useState(false);
    const [consents, setConsents] = useState({
        marketing: true,
        analytics: false,
        cookies: true
    });
    const [previousExports, setPreviousExports] = useState([]);
    const [currentExport, setCurrentExport] = useState(null);
    const [exportProgress, setExportProgress] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [totalExports, setTotalExports] = useState(0);
    const screens = useBreakpoint() || {};
    const [skeletonData, setSkeletonData] = useState([]);
    const [exportChipFilters, setExportChipFilters] = useState([]);
    const [normalizedExportChipFilters, setNormalizedExportChipFilters] = useState({});

    // Socket setup
    const { connectionState } = useSocket();

    // Keep track of pending exports to subscribe to them
    const [pendingExports, setPendingExports] = useState([]);

    // Derive active export IDs from both current export and any pending exports
    const activeExportIds = useMemo(() => {
        const ids = [];
        if (currentExport) {
            ids.push(currentExport.id);
        }
        pendingExports.forEach(exp => {
            if (!ids.includes(exp.id)) {
                ids.push(exp.id);
            }
        });
        return ids;
    }, [currentExport, pendingExports]);

    // Subscribe to active export IDs, sending the creation time if available
    const exportFromDate = useMemo(() => currentExport?.created_at ?? null, [currentExport]);
    useSocketSubscription(Resource.DATA_EXPORT, activeExportIds, false, exportFromDate);

    // Initial fetch to check for pending exports
    useEffect(() => {
        const checkForPendingExports = async () => {
            try {
                const { data, metadata } = await getPreviousExports({ page: 1, page_size: 100 });
                const pending = data.filter(export_ =>
                    export_.status === 'pending' ||
                    export_.status === 'processing' ||
                    export_.progress < 100
                );

                if (pending.length > 0) {
                    setPendingExports(pending);
                    // Set the first pending export as current if we don't already have one
                    if (!currentExport) {
                        setCurrentExport(pending[0]);
                        setExportProgress(pending[0].progress || 0);
                    }
                }

                // Also update our regular export list while we're at it
                setPreviousExports(data);
                setTotalExports(metadata.total);
            } catch (error) {
                handleError(error);
            }
        };

        checkForPendingExports();
    }, []);

    // Regular export updates
    useEffect(() => {
        if (isExportSectionExpanded) {
            fetchPreviousExports();
        }
    }, [isExportSectionExpanded, currentPage, pageSize, normalizedExportChipFilters]);

    const fetchPreviousExports = async () => {
        setLoading(true);
        try {
            const { data, metadata } = await getPreviousExports({
                page: currentPage,
                page_size: pageSize,
                filters: normalizedExportChipFilters,
            });
            setPreviousExports(data);
            setTotalExports(metadata.total);

            // Update pending exports list with any new pending exports
            const pending = data.filter(export_ =>
                export_.status === 'pending' ||
                export_.status === 'processing' ||
                export_.progress < 100
            );

            if (pending.length > 0) {
                setPendingExports(prev => {
                    const newPending = [...prev];
                    pending.forEach(p => {
                        if (!newPending.some(np => np.id === p.id)) {
                            newPending.push(p);
                        }
                    });
                    return newPending;
                });
            }
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    // Handle export progress updates from socket
    const handleExportProgress = useCallback((data) => {
        // Find if this is our pending export
        const pendingExport = pendingExports.find(exp => exp.id === data.id);

        // If this is our current export or a pending one, update its progress
        if ((currentExport && data.id === currentExport.id) || pendingExport) {
            // If it's the current export, update the progress state and status message
            if (currentExport && data.id === currentExport.id) {
                setExportProgress(data.progress || 0);
                setCurrentExport(prev => ({
                    ...prev,
                    status_message: data.status_message,
                    status: data.status
                }));
            }

            // Update the pending exports list
            setPendingExports(prev => {
                const updated = prev.map(exp =>
                    exp.id === data.id ? { ...exp, ...data } : exp
                );

                // Filter out completed exports
                return updated.filter(exp =>
                    exp.status !== 'completed' &&
                    exp.status !== 'failed' &&
                    exp.progress < 100
                );
            });

            // If export is complete, show notification
            if (data.progress >= 100 || data.status === 'completed') {
                notification.success({
                    message: t('export_completed'),
                    description: t('export_ready_to_download')
                });
                fetchPreviousExports();

                // If this was the current export, clear it
                if (currentExport && data.id === currentExport.id) {
                    setCurrentExport(null);

                    // If we have other pending exports, set the first one as current
                    const remainingPending = pendingExports.filter(exp => exp.id !== data.id);
                    if (remainingPending.length > 0) {
                        setCurrentExport(remainingPending[0]);
                        setExportProgress(remainingPending[0].progress || 0);
                    }
                }
            }
        }
    }, [currentExport, pendingExports, t, fetchPreviousExports]);

    // Handle newly created exports
    const handleExportCreated = useCallback((data) => {
        fetchPreviousExports();
    }, [fetchPreviousExports]);

    // Handle exports that were updated
    const handleExportUpdated = useCallback((data) => {
        // If this is our current export, update the progress
        if (currentExport && data.id === currentExport.id) {
            handleExportProgress(data);
        }

        // Update the export in the list if visible
        setPreviousExports(prev =>
            prev.map(exp => exp.id === data.id ? data : exp)
        );
    }, [currentExport, handleExportProgress]);

    // Handle exports that were deleted
    const handleExportDeleted = useCallback((data) => {
        fetchPreviousExports();

        // If the deleted export was our current one, clear it
        if (currentExport && data.id === currentExport.id) {
            setCurrentExport(null);
        }
    }, [currentExport, fetchPreviousExports]);

    useSocketEvent(Resource.DATA_EXPORT, Action.UPDATE, handleExportUpdated);

    const handleExport = async () => {
        try {
            setExporting(true);
            const { data } = await requestDataExport();

            setCurrentExport(data);
            setExportProgress(0);
            setIsExportSectionExpanded(true);

            notification.info({
                message: t('export_started'),
                description: t('export_progress_tracking')
            });

            setIsExportVisible(false);
        } catch (error) {
            handleError(error);
        } finally {
            setExporting(false);
        }
    };

    const handleDeleteExport = async (exportId) => {
        try {
            setDeletingExport(true);
            await deleteExport(exportId);
            notification.success({
                message: t('export_deleted'),
                description: t('export_deleted_successfully')
            });
            setExportToDelete(null);
            setIsDeleteExportModalVisible(false);
            fetchPreviousExports();
        } catch (error) {
            handleError(error);
        } finally {
            setDeletingExport(false);
        }
    };

    const showDeleteConfirmation = (exportRecord) => {
        setExportToDelete(exportRecord);
        setIsDeleteExportModalVisible(true);
    };

    const handleDelete = async () => {
        try {
            setDeletingAccount(true);
            await form.validateFields();

            // Call the deleteAccount service
            await deleteAccount();

            notification.warning({
                message: t('account_deletion_scheduled'),
                description: t('account_deletion_description')
            });

            // Redirect to login page after account deletion is scheduled
            setTimeout(async () => {
                try {
                    // Logout from Facebook if applicable
                    await fbLogout();
                    // Clear auth data
                    clearAuthData();
                    // Redirect to login
                    navigate('/login');
                } catch (error) {
                    handleError(error);
                    navigate('/login');
                }
            }, 2000);

            setIsDeleteVisible(false);
        } catch (error) {
            if (error.response) {
                handleError(error);
            } else {
                // Form validation error - handled by form UI
            }
        } finally {
            setDeletingAccount(false);
        }
    };

    const handleConsentUpdate = (consentType, checked) => {
        const updatedConsents = { ...consents, [consentType]: checked };
        setConsents(updatedConsents);

        updateConsents(updatedConsents)
            .then(() => {
                notification.info({
                    message: t('consent_updated'),
                    description: t('consent_preferences_saved', { consentType })
                });
            })
            .catch(error => {
                handleError(error);
                // Revert UI state if API call fails
                setConsents(consents);
            });
    };

    const dataPortabilityItems = [
        { title: t('personal_information'), description: t('personal_information_description') },
        { title: t('activity_history'), description: t('activity_history_description') },
        { title: t('connected_services'), description: t('connected_services_description') },
    ];

    const exportColumns = [
        {
            title: t('id'),
            dataIndex: 'id',
            key: 'id',
            render: (id) => (
                <div style={{ fontSize: '12px' }}>
                    {id}
                </div>
            )
        },
        {
            title: t('date_requested'),
            dataIndex: 'created_at',
            key: 'created_at',
            render: date => (
                <div style={{ fontSize: '12px' }}>
                    {new Date(date).toLocaleString()}
                </div>
            )
        },
        {
            title: t('completion_date'),
            dataIndex: 'completed_at',
            key: 'completed_at',
            render: date => (
                <div style={{ fontSize: '12px' }}>
                    {date ? new Date(date).toLocaleString() : '-'}
                </div>
            )
        },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            render: (status, record) => {
                // If this is the current export, show progress
                if (currentExport && record.id === currentExport.id) {
                    return (
                        <Tooltip title={record.status_message || currentExport.status_message}>
                            <div style={{ width: 200 }}>
                                <Progress
                                    percent={Math.round(record.progress || exportProgress)}
                                    status="active"
                                    size="small"
                                />
                            </div>
                        </Tooltip>
                    );
                }

                return (
                    <Tooltip title={record.status_message}>
                        <Tag color={
                            status === 'completed' ? 'green' :
                                status === 'processing' ? 'blue' :
                                    status === 'failed' ? 'red' : 'orange'
                        }>
                            {typeof status === 'string' ? t(status, { defaultValue: status }) : ''}
                        </Tag>
                    </Tooltip>
                );
            }
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <RowActions
                    screens={screens}
                    primary={{
                        label: t('download'),
                        icon: <DownloadOutlined />,
                        onClick: () => {
                            if (record.status === 'completed' && record.download_url) {
                                const filename = record.download_url.substring(record.download_url.lastIndexOf('/') + 1) || `export_${record.id}.zip`;
                                downloadFile(record.download_url, filename);
                            }
                        },
                        disabled: record.status !== 'completed' || !record.download_url,
                        tooltip: t('download')
                    }}
                    items={[
                        {
                            key: 'delete',
                            label: t('delete'),
                            icon: <DeleteOutlined />,
                            danger: true,
                            onClick: () => showDeleteConfirmation(record)
                        }
                    ]}
                />
            )
        }
    ];

    // Combine current export and previous exports for display
    const allExports = useMemo(() => {
        // If no previous exports data yet, return empty array or just current export
        if (!previousExports || !Array.isArray(previousExports)) {
            return currentExport ? [{ ...currentExport, progress: exportProgress }] : [];
        }

        if (!currentExport) return previousExports;

        // Check if current export is already in the list
        const exportExists = previousExports.some(exp => exp.id === currentExport.id);

        if (exportExists) {
            // Update the export in the list with current progress info
            return previousExports.map(exp =>
                exp.id === currentExport.id
                    ? { ...exp, progress: exportProgress }
                    : exp
            );
        } else {
            // Add current export to the top of the list
            return [{ ...currentExport, progress: exportProgress }, ...previousExports];
        }
    }, [currentExport, previousExports, exportProgress]);

    // Add this effect to update skeleton data when page size changes
    useEffect(() => {
        setSkeletonData(Array(pageSize).fill({}));
    }, [pageSize]);

    const cardStyle = {
        backgroundColor: token.colorBgContainer,
        color: token.colorText,
        border: isDarkMode ? `1px solid ${token.colorBorder}` : 'none',
    };

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Content style={{ maxWidth: '100%', margin: 0 }}>
                {/* Data Exports Section */}
                <Card
                    style={{ marginBottom: 16, ...cardStyle }}
                    title={
                        <Flex align="center" justify="space-between">
                            <span>{t('data_exports')}</span>
                        </Flex>
                    }
                    extra={
                        <Button
                            type="link"
                            onClick={() => setIsExportVisible(true)}
                            icon={<PlusOutlined />}
                        >
                            <span className="mobile-hide-text">{t('request_data_export')}</span>
                        </Button>
                    }
                >
                    <div style={{ marginBottom: 12 }}>
                        <SharedFilterBar
                            fields={dataExportFilterFields}
                            value={exportChipFilters}
                            onChange={(nextFilters, normalized) => {
                                setExportChipFilters(nextFilters);
                                setNormalizedExportChipFilters(normalized);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                    {loading ? (
                        <AppTable
                            columns={exportColumns.map(column => ({
                                ...column,
                                render: () => <Skeleton.Input active size="small" />
                            }))}
                            dataSource={Array(pageSize).fill({})}
                            pagination={false}
                            rowKey={(_, index) => index}
                        />
                    ) : (
                        <div style={{ overflowY: 'auto' }}>
                            <AppTable
                                columns={exportColumns}
                                dataSource={allExports}
                                rowKey="id"
                                pagination={{
                                    current: currentPage,
                                    pageSize: pageSize,
                                    total: totalExports + (currentExport && previousExports && Array.isArray(previousExports) && !previousExports.some(e => e.id === currentExport?.id) ? 1 : 0),
                                    onChange: (page, pageSize) => {
                                        setCurrentPage(page);
                                        setPageSize(pageSize);
                                    },
                                    showSizeChanger: true,
                                    pageSizeOptions: [5, 10, 25],
                                    onShowSizeChange: (current, size) => {
                                        setPageSize(size);
                                    }
                                }}
                            />
                        </div>
                    )}
                </Card>

                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Card bordered={false} style={{ marginBottom: 24, ...cardStyle }} title={<Title style={{ margin: 0 }} level={4}>{t('account_deletion')}</Title>}>
                            {loading ? (
                                <Skeleton active paragraph={{ rows: 2 }} />
                            ) : (
                                <>
                                    <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                                        {t('account_deletion_warning')}
                                    </Text>
                                    <Button
                                        danger
                                        onClick={() => setIsDeleteVisible(true)}
                                        block={screens.xs}
                                    >
                                        {t('initiate_account_deletion')}
                                    </Button>
                                </>
                            )}
                        </Card>
                    </Col>
                    <Col xs={24} md={12}>
                        <Card bordered={false} style={{ marginBottom: 24, ...cardStyle }} title={<Title style={{ margin: 0 }} level={4}>{t('legal_documents')}</Title>}>
                            {loading ? (
                                <Skeleton active paragraph={{ rows: 2 }} />
                            ) : (
                                <List
                                    itemLayout="horizontal"
                                    style={{ marginTop: 0 }}
                                    dataSource={[
                                        {
                                            title: t('privacy_policy'),
                                            link: '../privacy.html'
                                        },
                                        {
                                            title: t('terms_of_service'),
                                            link: '../terms.html'
                                        }
                                    ]}
                                    renderItem={(item, index) => (
                                        <List.Item style={index === 0 ? { paddingTop: 0 } : {}}>
                                            <Link href={item.link} target="_blank">
                                                {item.title}
                                            </Link>
                                        </List.Item>
                                    )}
                                />
                            )}
                        </Card>
                    </Col>
                </Row>

                {/* Delete Account Modal */}
                <ResponsiveModal
                    title={t('confirm_account_deletion')}
                    open={isDeleteVisible}
                    onOk={handleDelete}
                    onCancel={() => {
                        setIsDeleteVisible(false);
                        form.resetFields();
                    }}
                    okButtonProps={{ danger: true }}
                    confirmLoading={deletingAccount}
                    desktopWidth={520}
                    okText={t('confirm')}
                >
                    <Form form={form} layout="vertical">
                        <p>{t('permanent_data_erasure_warning')}</p>
                        <Form.Item
                            name="confirmation"
                            label={t('type_delete_my_account')}
                            rules={[
                                {
                                    required: true,
                                    message: t('please_type_confirmation_phrase'),
                                    validator: (_, value) =>
                                        value === t('delete_account_confirmation_phrase') ?
                                            Promise.resolve() : Promise.reject()
                                }
                            ]}
                        >
                            <Input placeholder={t('enter_confirmation_phrase')} />
                        </Form.Item>
                    </Form>
                </ResponsiveModal>

                {/* Export Request Modal */}
                <ResponsiveModal
                    title={t('data_export_request')}
                    open={isExportVisible}
                    onOk={handleExport}
                    onCancel={() => setIsExportVisible(false)}
                    confirmLoading={exporting}
                    desktopWidth={520}
                    okText={t('proceed')}
                >
                    <p>{t('export_includes_description')}</p>
                </ResponsiveModal>

                {/* Delete Export Confirmation Modal */}
                <ResponsiveModal
                    title={t('delete_export')}
                    open={isDeleteExportModalVisible}
                    onCancel={() => setIsDeleteExportModalVisible(false)}
                    onOk={() => exportToDelete && handleDeleteExport(exportToDelete.id)}
                    okText={t('confirm')}
                    cancelText={t('cancel')}
                    confirmLoading={deletingExport}
                    okButtonProps={{ danger: true }}
                >
                    <p>{t('are_you_sure_you_want_to_delete_this_export')}</p>
                </ResponsiveModal>

            </Content>
        </Layout>
    );
};

export default Settings;
